import { Injectable, Inject, LOCALE_ID } from '@angular/core';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import { map, switchMap, retryWhen, delay, mergeMap, catchError, take } from 'rxjs/operators';
import { get, isEmpty, set } from 'lodash';
import { SearchService } from '@ispm/shared/dynamic-form/services/search.service';
import { formatDate } from '@angular/common';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { BiodesixTestType } from '../constants/revisionStatus.enum';
import { ProgressionService } from '@ispm/shared/services/progression.service';
import { Progression } from '@ispm-shared/modules/common';
import { IOrderDetail } from '../interfaces';
import { OrderService } from './order.service';
import moment from 'moment';

@Injectable()
export class OrderSFTPService {

    constructor(private _ispmHttpClient: IspmHttpClient,
        private _contextProgressionService: ProgressionService,
        private _search: SearchService,
        private _orderService: OrderService,
        @Inject(LOCALE_ID) private locale: string) { }

    /**
    Connect to ispm-web for SFTP
    @param : diagnosticOrderDetails - Contains the formData
    */
    copyToSFTP(diagnosticOrderDetails: any) {

        let remoteFilename = diagnosticOrderDetails.identifiers[0].value
            + '_'
            + formatDate(diagnosticOrderDetails.meta.created, 'yyyyMMdd_HHmmss', this.locale)
            + '_Order.json';

        console.log('remoteFilename', remoteFilename);

        return this.creatingRequestBody(diagnosticOrderDetails).pipe(
            switchMap(response => {
                return this._ispmHttpClient.post('fileUtil/copyToSFTP', response, true, {}, { 'remoteFileName': remoteFilename })
                    .pipe(
                        this.handleRetryError(2000, 3),
                        take(1)
                    );
            })
        )
    }

    /**
    Creates the requestBody for SFTP in given JSON format
     @param : diagnosticOrderDetails - copies required deatils from diagnosticOrderDetails to requestBody
    */
    creatingRequestBody(diagnosticOrderDetails: any) {
        let requestBody = {};
        const getCountries = this._search.query('/valueset/getcountries', '');
        const getStates =  this._search.query('/valueset/states', '');
        return combineLatest([getCountries, getStates]).pipe(
            map(([countries, states]) => {
                requestBody = {
                    'version': '1.0.0',
                    'order_information': {
                        'order_id': diagnosticOrderDetails.identifiers[0].value
                    },
                    'facility': {
                        'name': diagnosticOrderDetails.facilityDetails?.facilityName || '',
                        'npi': diagnosticOrderDetails.facilityDetails?.facilityNpi,
                        'address': {
                            'street': diagnosticOrderDetails.facilityDetails?.fAddressStreet || '',
                            'city': diagnosticOrderDetails.facilityDetails?.fAddressCity || '',
                            'district': diagnosticOrderDetails.facilityDetails?.fAddressDistrict || '',
                            'state': this.getStateDetails(states, diagnosticOrderDetails.facilityDetails?.fAddressState, 'alpha2Code'),
                            'postal_code': diagnosticOrderDetails.facilityDetails?.fPostalCode || '',
                            'country': diagnosticOrderDetails.facilityDetails?.fCountry
                                ? this.getCountryDetails(countries, diagnosticOrderDetails.facilityDetails?.fCountry, 'alpha3Code') : ''
                        }
                    },
                    'physician': {
                        'name': {
                            'first_name': diagnosticOrderDetails.biodesixOrderingPersonnel?.firstName || '',
                            'middle_name': diagnosticOrderDetails.biodesixOrderingPersonnel?.middleName || '',
                            'last_name': diagnosticOrderDetails.biodesixOrderingPersonnel?.lastName || ''
                        },
                        'npi': diagnosticOrderDetails.biodesixOrderingPersonnel?.npi,
                        'phone': this.formatPhoneNumber(diagnosticOrderDetails.biodesixOrderingPersonnel?.mobileNo),
                        'fax': this.formatPhoneNumber(diagnosticOrderDetails.biodesixOrderingPersonnel?.fax),
                        'email': diagnosticOrderDetails.biodesixOrderingPersonnel?.email || '',
                        'signed_date': this.setAndformatDateForSFTP(diagnosticOrderDetails.biodesixOrderingPersonnel?.signedDate),
                        'deliver_encrypted_email': diagnosticOrderDetails.biodesixOrderingPersonnel?.email ? true : false,
                        'deliver_fax': diagnosticOrderDetails.biodesixOrderingPersonnel?.fax ? true : false,
                    },
                    'test_menu': {
                        'name': this.getBiodesixTestType(diagnosticOrderDetails.testMenu?.testMenuName.code)
                    },
                    'patient': {
                        'id': diagnosticOrderDetails.patientDetails?.mrn || '',
                        'name': {
                            'first_name': diagnosticOrderDetails.patientDetails?.patientFirstName,
                            'middle_name': diagnosticOrderDetails.patientDetails?.patientMiddleName,
                            'last_name': diagnosticOrderDetails.patientDetails?.patientLastName
                        },
                        'dob': this.formatDateForSFTP(diagnosticOrderDetails.patientDetails?.dob),
                        'gender': this.formatGender(diagnosticOrderDetails.patientDetails?.pgender),
                        'phone': this.formatPhoneNumber(diagnosticOrderDetails.patientDetails?.mobileNo),
                        'email': diagnosticOrderDetails.patientDetails?.email || '',
                        'mrn': diagnosticOrderDetails.patientDetails?.mrn || '',
                        'address': {
                            'street': diagnosticOrderDetails.patientDetails?.addressStreet || '',
                            'city': diagnosticOrderDetails.patientDetails?.addressCity || '',
                            'state': this.getStateDetails(states, diagnosticOrderDetails.patientDetails?.addressState, 'alpha2Code'),
                            'postal_code': diagnosticOrderDetails.patientDetails?.postalCode || '',
                            'country': this.getCountryDetails(countries, diagnosticOrderDetails.patientDetails?.country, 'alpha3Code')
                        }

          },
          'clinical_factors': {
            'nodule_in_upper_lobe': diagnosticOrderDetails.noduleClinicalFactors?.noduleInUpperLobe?.code || '',
            'smoking_status': diagnosticOrderDetails.smokingClinicalFactors?.smokingStatus?.display || '',
            'nodule_diameter': diagnosticOrderDetails.noduleClinicalFactors?.noduleDiameter,
            'nodule_spiculated': diagnosticOrderDetails.noduleClinicalFactors?.noduleSpiculated?.code || '',
            'history_of_cancer': diagnosticOrderDetails.clinicalFactors?.historyOfCancer?.display|| '',
            'icd_diagnosis': diagnosticOrderDetails.clinicalFactors?.ICD10?.icdDiagnosis ? [this.getIcdDiagnosis(diagnosticOrderDetails.clinicalFactors)]: []
          },
          'contacts': this.getContactDetails(diagnosticOrderDetails.contacts),
          'draw_information': diagnosticOrderDetails.drawInfo?.location?.display === 'Home Phlebotomy'
                              ? { 'location': diagnosticOrderDetails.drawInfo?.location?.display || '' }
                              : {
                                'location': diagnosticOrderDetails.drawInfo?.location?.display || '',
                                'collected_by': diagnosticOrderDetails.drawInfo?.collectedBy || '',
                                'draw_date': this.formatDateForSFTP(diagnosticOrderDetails.drawInfo?.drawDate)
                              },
          'patient_insurance': this.getPatientInsuranceDetails(diagnosticOrderDetails.patientInsurance)
        }
        return requestBody;
      })
    )
  }

  /**
   * Creates expected contacts object, sent as part of SFTP requestBody
   * @param contactDetails contains contact information
   * @returns contacts object in required biodesix format
   */
   getContactDetails(contactDetails){
    if((contactDetails?.contacts?.code) === 'No'){
      return [];
    }else{
      return [
        {
          'type': contactDetails?.contactType?.display,
          'name': contactDetails?.contactName,
          'phone': this.formatPhoneNumber(contactDetails?.contactPhone),
          'fax': this.formatPhoneNumber(contactDetails?.contactFax),
          'email': contactDetails?.contactEmail,
          'deliver_encrypted_email': contactDetails?.contactEmail ? true : false,
          'deliver_fax': contactDetails?.contactFax ? true : false
        }
      ]
    }
  }

  /**
   * Creates expected patient_insurance object, sent as part of SFTP requestBody
   * @param patientInsuranceDetails contains patient Insurance details
   * @returns patient_insurance object in required biodesix format
   */
  getPatientInsuranceDetails(patientInsuranceDetails){
    if((patientInsuranceDetails?.patientInsurance?.code) === 'No'){
      return [];
    }else{
      return [
        {
          'type': patientInsuranceDetails?.type?.display,
          'provider': patientInsuranceDetails?.provider,
          'policy_no': patientInsuranceDetails?.policyNo,
          'group_no': patientInsuranceDetails?.groupNo,
          'insured_name': {
            'first_name': patientInsuranceDetails?.insuredFirstName,
            'last_name': patientInsuranceDetails?.insuredLastName
          },
          ...(typeof patientInsuranceDetails.insuredDob !== 'undefined') && (patientInsuranceDetails.insuredDob !== null) && { insured_dob: this.formatDateForSFTP(patientInsuranceDetails?.insuredDob) },
          'relationship': patientInsuranceDetails?.relationship?.display
        }
      ]
    }
  }

  getIcdDiagnosis(clinicalFactors:any){
    return clinicalFactors?.ICD10?.icdDiagnosis === 'Others' ? clinicalFactors.ICD10.icdOtherValue?.code : clinicalFactors.ICD10.icdDiagnosis;
  }

    getCountryDetails(countries: any, country: string, path: string) {
        if (country) {
            let countryData = countries.find(res => res.name == country);
            return get(countryData, path, '');
        } else
            return '';
    }

    private getStateDetails(states: any, state: string, alpha2Code: string){
        let stateData = states.find(res => res.name == state || res.alpha2Code == state);
        return stateData ? get(stateData, alpha2Code, state) : state;

        }

    formatDateForSFTP(date: string) {
        let value = !isEmpty(date) ? formatDate(date, 'yyyy-MM-dd', this.locale) : '';
        return value;
    }

    setAndformatDateForSFTP(date: string) {
        return (!!date && date.trim())
            ? this.formatDateForSFTP(date)
            : moment(new Date()).format("YYYY-MM-DD");
    }

    formatGender(gender: string) {
        switch (gender) {
            case 'Female':
                return 'F';
            case 'Male':
                return 'M';
            default:
                return ''
        }
    }

    formatPhoneNumber(number: string) {
        if (!isEmpty(number)) {
            return number.replace(/[()]+/g, '');
        }
        else
            return '';
    }

    private getBiodesixTestType(testType: string) {

        const cdtTest = 'CDT';
        const xl2Test = 'XL2';
        const nodifyTest = 'Nodify';

        switch (testType) {
            case BiodesixTestType.CDT:
                return [cdtTest];
            case BiodesixTestType.XL2:
                return [xl2Test];
            case BiodesixTestType.Nodify:
                return [nodifyTest]
            case BiodesixTestType.CDTXL2:
                return [cdtTest, xl2Test];
            default:
                return [];
        }
    }

    /**
     * Create a entry to Progression resource
     * @param diagnosticOrderDetails holds the diagnostic order details
     * @param isSFTPPublished boolean value and depends on successful posting of sftp
     * @returns Observable of Progression
     */
    createProgression(diagnosticOrderDetails: any, isSFTPPublished: boolean): Observable<Progression> {
        const expectedResults = this.createExpectedResults(get(diagnosticOrderDetails, 'testMenu.testMenuName.code', ''));
        const additionalProperties = {
            diagnosticOrderId: diagnosticOrderDetails.id,
            fileName: diagnosticOrderDetails.identifiers[0].value,
            overallResultStatus: null,
            lastUpdatedOn: diagnosticOrderDetails.meta.created,
            SFTPPublishStatus: isSFTPPublished,
            eventIds: [],
            expectedResults: expectedResults
        };
        return this._contextProgressionService.create('BiodesixResult', additionalProperties);
    }

    private setSignedDate(orderDetails: IOrderDetail[]) {
        if (orderDetails.length > 0) {
            const signedDate: string = moment(new Date()).format("DD MMM YYYY");
            orderDetails[0].biodesixOrderingPersonnel = {
                ...orderDetails[0].biodesixOrderingPersonnel,
                "signedDate": signedDate
            };

        }
        return orderDetails;
    }

    /**
     * Updating the Diagnostic and Progression resource
     * @param orderDetails Array list of diagnostic order
     * @returns Observable of array list of IOrderDetail and Progression
     */
    updateResources(orderDetails: IOrderDetail[]): Observable<{ orderDetailsResponse: IOrderDetail[], progression: Progression }> {
        orderDetails = this.setSignedDate(orderDetails);
        set(orderDetails[0], 'orderStatus', 'FINAL');
        set(orderDetails[0], 'derivedStatus.code', 'IN_PROCESS');
        set(orderDetails[0], 'derivedStatus.display', 'In Progress');
        return this._orderService.updateOrderDetails(orderDetails).pipe(
            switchMap(orderDetailsResponse => {
                return this.createProgression(orderDetails[0], true).pipe(
                    map(progression => {
                        return {
                            orderDetailsResponse,
                            progression
                        }
                    })
                );
            }),
            catchError(err => {
                return throwError(err);
            })
        );
    }

    /**
   * Creates expected object that is posted to Progression resource
   * @param testType Type of test
   */
    private createExpectedResults(testType: string) {
        const orderStatus = {
            "biodesixTestId": "",
            "testStatus": "",
            "resultStatus": "",
            "attachments": []
        };
        const CDTTestObject = [{
            "testName": "CDT",
            ...orderStatus
        }];
        const XL2TestObject = [{
            "testName": "XL2",
            ...orderStatus
        }];
        switch (testType) {
            case BiodesixTestType.CDT:
                return CDTTestObject;
            case BiodesixTestType.XL2:
                return XL2TestObject;
            case BiodesixTestType.Nodify:
            case BiodesixTestType.CDTXL2:
                return [...CDTTestObject, ...XL2TestObject];
            default:
                return [...CDTTestObject, ...XL2TestObject];
        };
    }

    /**
     * Retry the request for specified number of attempts
     * @param delayTime Time shifts each item by specified amount of milliseconds
     * @param attempLimit Retry limit
     * @returns Observable
     */
    private handleRetryError(delayTime: number, attempLimit: number) {
        let exceedAttemptLimit = attempLimit;
        return retryWhen((error) => {
            return error.pipe(
                delay(delayTime),
                mergeMap((err) => {
                    return exceedAttemptLimit-- > 0 ? of(err) : throwError(err);
                })
            );
        });
    }

  /**
   * Fetch the progression data
   * @param diagnostic order id to fetch progression record
   */
  allProgressionStatus(selectedProgressionId) {
    return this._ispmHttpClient.get(`entity-tree/Progression?diagnosticOrderId=${selectedProgressionId}`).pipe(map(progression => progression[0]))
  }
}

